'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getHallOfFame, type HallOfFameData, type HallOfFameEntry } from '@/lib/actions';
import { Loader2, Trophy, Users, Church, Music2, FileSignature, Clock, Crown, ListMusic, Heart, Coins } from 'lucide-react';
import { cn } from '@/lib/utils';

const MEDALLA = ['#facc15', '#cbd5e1', '#d97706'];

function Podio({ pos }: { pos: number }) {
  if (pos < 3) {
    return (
      <span
        className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-xs font-black text-black"
        style={{ background: MEDALLA[pos], boxShadow: `0 0 14px ${MEDALLA[pos]}66` }}
      >
        {pos + 1}
      </span>
    );
  }
  return (
    <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-white/5 text-xs font-bold text-muted-foreground">
      {pos + 1}
    </span>
  );
}

function Ranking({
  icon,
  title,
  subtitle,
  entries,
  unidad,
  color,
  linkBase,
  avatarPorUuid,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  entries: HallOfFameEntry[];
  unidad: string;
  color: string;
  /** Si se pasa, cada fila enlaza a `${linkBase}/${id}`. */
  linkBase?: string;
  /** Muestra la cabeza de Minecraft en vez del escudo. */
  avatarPorUuid?: boolean;
}) {
  if (!entries?.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-5"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl" style={{ background: `${color}22` }} />

      <div className="relative mb-4 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl border" style={{ borderColor: `${color}55`, background: `${color}1a`, color }}>
          {icon}
        </div>
        <div className="min-w-0">
          <h2 className="font-headline text-lg font-bold">{title}</h2>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <ol className="relative space-y-1.5">
        {entries.map((e, i) => {
          const fila = (
            <>
              <Podio pos={i} />
              {avatarPorUuid ? (
                <img
                  src={`https://mc-heads.net/avatar/${e.uuid || e.nombre}/28`}
                  alt=""
                  className="h-7 w-7 flex-shrink-0 rounded-md"
                  loading="lazy"
                />
              ) : e.escudoUrl ? (
                <img src={e.escudoUrl} alt="" className="h-7 w-7 flex-shrink-0 object-contain" loading="lazy" />
              ) : (
                <span className="h-7 w-7 flex-shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{e.nombre}</span>
              <span className="flex-shrink-0 text-sm font-black tabular-nums" style={{ color: i < 3 ? color : undefined }}>
                {e.valor.toLocaleString('es-ES')}
                <span className="ml-1 text-[0.65rem] font-medium uppercase text-muted-foreground">{unidad}</span>
              </span>
            </>
          );

          const clase = cn(
            'flex items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 transition-colors',
            i < 3 ? 'bg-white/[0.04]' : 'hover:bg-white/[0.03]',
            linkBase && 'hover:border-white/15',
          );

          return (
            <li key={e.id}>
              {linkBase ? (
                <Link to={`${linkBase}/${e.id}`} className={clase}>{fila}</Link>
              ) : (
                <div className={clase}>{fila}</div>
              )}
            </li>
          );
        })}
      </ol>
    </motion.section>
  );
}

function Total({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl border border-white/8 bg-card/40 px-5 py-4">
      <span className="text-primary">{icon}</span>
      <span className="font-headline text-2xl font-black">{value.toLocaleString('es-ES')}</span>
      <span className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">{label}</span>
    </div>
  );
}

export default function HallOfFamePage() {
  const [data, setData] = useState<HallOfFameData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getHallOfFame().then((d) => {
      if (!cancelled) { setData(d); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6 pb-10">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-primary/15 via-card/60 to-transparent p-7">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl border border-primary/30 bg-primary/10">
            <Trophy className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-headline text-3xl font-bold md:text-4xl">Hall of Fame</h1>
            <p className="text-sm text-muted-foreground">
              Los números de Jartiland: quién manda en la red social, en el banco, en las calles y en el servidor.
            </p>
          </div>
        </div>

        {data && (
          <div className="relative mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Total icon={<Church className="h-5 w-5" />} value={data.totales.hermandades} label="Hermandades" />
            <Total icon={<Music2 className="h-5 w-5" />} value={data.totales.bandas} label="Bandas" />
            <Total icon={<Users className="h-5 w-5" />} value={data.totales.hermanos} label="Hermanos" />
            <Total icon={<FileSignature className="h-5 w-5" />} value={data.totales.contratos} label="Contratos" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      ) : !data ? (
        <div className="py-32 text-center">
          <p className="text-muted-foreground">No se han podido cargar los rankings ahora mismo. Prueba de nuevo en un rato.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-5 xl:grid-cols-2">
            <Ranking
              icon={<Heart className="h-5 w-5" />}
              title="Más me gustas en la red social"
              subtitle="Hermandades, bandas y organizaciones, sumando también las colaboraciones"
              entries={data.perfilesPorMeGusta}
              unidad="me gustas"
              color="#f43f5e"
            />
            <Ranking
              icon={<Coins className="h-5 w-5" />}
              title="Mayores fortunas"
              subtitle="Oro en todas las cuentas a las que tiene acceso (propias y compartidas)"
              entries={data.fortunas}
              unidad="J"
              color="#fbbf24"
              // Cada fila es un ciudadano: se enlaza a su carnet.
              linkBase="/jartinet/carnet"
            />
            <Ranking
              icon={<Users className="h-5 w-5" />}
              title="Hermandades con más hermanos"
              subtitle="Las nóminas más grandes de la ciudad"
              entries={data.hermandadesPorHermanos}
              unidad="hermanos"
              color="#60a5fa"
              linkBase="/hermandades"
            />
            <Ranking
              icon={<Clock className="h-5 w-5" />}
              title="Más horas en el servidor"
              subtitle="Los que no se van nunca de Jartiland"
              entries={data.jugadoresPorHoras}
              unidad="horas"
              color="#34d399"
              avatarPorUuid
            />
            <Ranking
              icon={<FileSignature className="h-5 w-5" />}
              title="Bandas más contratadas"
              subtitle="Contratos firmados con hermandades y organizaciones"
              entries={data.bandasPorContratos}
              unidad="contratos"
              color="#fb923c"
              linkBase="/bandas"
            />
            <Ranking
              icon={<ListMusic className="h-5 w-5" />}
              title="Bandas con más repertorio"
              subtitle="Marchas registradas en su repertorio"
              entries={data.bandasPorRepertorio}
              unidad="marchas"
              color="#a78bfa"
              linkBase="/bandas"
            />
            <Ranking
              icon={<Crown className="h-5 w-5" />}
              title="Hermandades más antiguas"
              subtitle="Por año de fundación"
              entries={data.hermandadesMasAntiguas}
              unidad=""
              color="#f472b6"
              linkBase="/hermandades"
            />
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Los datos se calculan en el momento a partir del censo de hermandades, los me gustas de la red social, los saldos
            del banco, los contratos firmados y la telemetría del servidor.
          </p>
        </>
      )}
    </div>
  );
}
